/**
 * Invoice PDF document — generated by @react-pdf/renderer.
 *
 * Mirrors the look-and-feel of the HTML `InvoiceDetailModal` so the
 * downloaded PDF matches the on-screen view. Used for the "Download PDF"
 * button in the Billing tab and works identically on iOS Safari, Android
 * Chrome, and desktop browsers (the print-from-browser path is unreliable
 * on iOS in particular — see hindsight-accounting-suite for the same
 * dual-path approach).
 */

import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { siteConfig } from '@sighthost/config';
import type {
  AdminInvoiceRow,
  AdminInvoiceLine,
  AdminPaymentRow,
} from '@/domains/billing/services/adminInvoiceService';

interface CustomerForPdf {
  full_name: string;
  email: string | null;
  company: string | null;
  billing_email: string | null;
  billing_address_line1: string | null;
  billing_address_line2: string | null;
  billing_address_city: string | null;
  billing_address_postal_code: string | null;
  billing_address_country: string;
  registration_number: string | null;
  vat_number: string | null;
}

interface Props {
  invoice: AdminInvoiceRow;
  lines: AdminInvoiceLine[];
  customer: CustomerForPdf;
  payments: AdminPaymentRow[];
  /** Absolute URL of the company logo so @react-pdf can fetch it. */
  logoUrl: string;
}

const formatZAR = (amount: number): string =>
  new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
  }).format(amount);

const formatNumber = (n: number): string =>
  n.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: '#1f2937',
  },
  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  logo: {
    width: 150,
    height: 52,
    objectFit: 'contain',
  },
  docTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    textAlign: 'right',
  },
  docNumber: {
    fontSize: 10,
    color: '#4b5563',
    textAlign: 'right',
    marginTop: 2,
  },
  // Address grid
  addressGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  addressCol: {
    width: '48%',
  },
  sectionLabel: {
    fontSize: 8,
    color: '#4b5563',
    fontWeight: 'bold',
    marginBottom: 2,
  },
  addressLine: {
    fontSize: 8,
    color: '#1f2937',
    lineHeight: 1.3,
  },
  bold: {
    fontWeight: 'bold',
  },
  refLine: {
    fontSize: 8,
    color: '#4b5563',
    textAlign: 'right',
  },
  refValue: {
    fontWeight: 'bold',
    color: '#1f2937',
  },
  // Dates
  datesGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  dateCol: {
    width: '48%',
  },
  dateLabel: {
    fontSize: 8,
    color: '#4b5563',
  },
  dateValue: {
    fontSize: 8,
    color: '#1f2937',
    fontWeight: 'bold',
    marginBottom: 4,
  },
  // Line items
  table: {
    marginBottom: 10,
  },
  tableHead: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    paddingVertical: 4,
    paddingHorizontal: 4,
    fontSize: 8,
    fontWeight: 'bold',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 3,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e5e7eb',
    fontSize: 8,
  },
  tableRowAlt: {
    backgroundColor: '#fafafa',
  },
  colCode: { width: '12%' },
  colDesc: { width: '46%' },
  colQty: { width: '8%', textAlign: 'center' as const },
  colUnit: { width: '12%', textAlign: 'right' as const },
  colVat: { width: '8%', textAlign: 'center' as const },
  colTotal: { width: '14%', textAlign: 'right' as const },
  // Totals
  totalsBox: {
    alignSelf: 'flex-end',
    width: 180,
    fontSize: 9,
    marginBottom: 12,
    paddingTop: 6,
    borderTopWidth: 0.5,
    borderTopColor: '#d1d5db',
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  totalsRowFinal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: 'bold',
    fontSize: 11,
    paddingTop: 2,
  },
  // Sections
  section: {
    marginBottom: 10,
    paddingTop: 6,
    borderTopWidth: 0.5,
    borderTopColor: '#e5e7eb',
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 3,
  },
  bodyText: {
    fontSize: 8,
    color: '#4b5563',
    lineHeight: 1.4,
  },
  paymentTable: {
    marginTop: 4,
  },
  paymentHead: {
    flexDirection: 'row',
    fontSize: 8,
    fontWeight: 'bold',
    color: '#4b5563',
    marginBottom: 2,
  },
  paymentRow: {
    flexDirection: 'row',
    fontSize: 8,
    paddingVertical: 2,
    borderTopWidth: 0.5,
    borderTopColor: '#f3f4f6',
  },
  payDate: { width: '20%' },
  payMethod: { width: '20%' },
  payRef: { width: '40%', fontSize: 7, fontFamily: 'Courier' },
  payAmount: { width: '20%', textAlign: 'right' as const },
  // Footer
  footer: {
    position: 'absolute',
    bottom: 18,
    left: 28,
    right: 28,
    textAlign: 'center',
    fontSize: 7,
    color: '#6b7280',
    borderTopWidth: 0.5,
    borderTopColor: '#e5e7eb',
    paddingTop: 4,
  },
});

export function InvoicePdfDocument({
  invoice,
  lines,
  customer,
  payments,
  logoUrl,
}: Props) {
  // QM-5 (2026-07-17): quotations render with their own masthead + validity +
  // no-demand-for-payment footer; converted invoices reference the original QUO.
  const isQuotation = invoice.doc_type === 'quotation';
  const docTitle = isQuotation
    ? 'QUOTATION'
    : siteConfig.vatRegistered
      ? 'TAX INVOICE'
      : 'INVOICE';
  const billTo = customer.billing_email ?? customer.email ?? '—';
  const company = customer.company?.trim();
  const addressLines = [
    customer.billing_address_line1,
    customer.billing_address_line2,
    [customer.billing_address_city, customer.billing_address_postal_code]
      .filter(Boolean)
      .join(' '),
    customer.billing_address_country,
  ].filter((l): l is string => !!l && l.trim().length > 0);

  return (
    <Document
      title={`${isQuotation ? 'Quotation' : 'Invoice'} ${invoice.number}`}
      author={siteConfig.legalName}
      subject={`${isQuotation ? 'Quotation' : 'Invoice'} ${invoice.number}`}
    >
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.header}>
          <Image src={logoUrl} style={styles.logo} />
          <View>
            <Text style={styles.docTitle}>{docTitle}</Text>
            <Text style={styles.docNumber}>{invoice.number}</Text>
            {isQuotation && invoice.quote_expires_at && (
              <Text style={[styles.docNumber, { color: '#b45309' }]}>
                Valid until{' '}
                {new Date(invoice.quote_expires_at).toLocaleDateString('en-ZA')}
              </Text>
            )}
            {!isQuotation && invoice.quote_number && (
              <Text style={styles.docNumber}>
                Ref: quotation {invoice.quote_number}
              </Text>
            )}
          </View>
        </View>

        {/* ADDRESS GRID */}
        <View style={styles.addressGrid}>
          <View style={styles.addressCol}>
            <Text style={styles.sectionLabel}>Company:</Text>
            <Text style={[styles.addressLine, styles.bold]}>
              {siteConfig.legalName}
            </Text>
            <Text style={styles.addressLine}>
              {siteConfig.address.street}
            </Text>
            <Text style={styles.addressLine}>
              {siteConfig.address.locality}
            </Text>
            <Text style={styles.addressLine}>
              {siteConfig.address.city} {siteConfig.address.postalCode}
            </Text>
            <Text style={styles.addressLine}>
              {siteConfig.address.countryName}
            </Text>
            <Text style={styles.addressLine}>{siteConfig.phoneDisplay}</Text>
            <Text style={styles.addressLine}>{siteConfig.billingEmail}</Text>
            <Text style={styles.addressLine}>
              Reg: {siteConfig.registration}
            </Text>
            {siteConfig.vatRegistered && siteConfig.vatNumber && (
              <Text style={styles.addressLine}>
                VAT: {siteConfig.vatNumber}
              </Text>
            )}
          </View>

          <View style={styles.addressCol}>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 2,
              }}
            >
              <Text style={styles.sectionLabel}>Bill To:</Text>
              <Text style={styles.refLine}>
                REF: <Text style={styles.refValue}>{invoice.number}</Text>
              </Text>
            </View>
            <Text style={[styles.addressLine, styles.bold]}>
              {company ?? customer.full_name}
            </Text>
            {company && (
              <Text style={[styles.addressLine, { color: '#374151' }]}>
                Att: {customer.full_name}
              </Text>
            )}
            {addressLines.map((l, i) => (
              <Text key={i} style={styles.addressLine}>
                {l}
              </Text>
            ))}
            <Text style={styles.addressLine}>{billTo}</Text>
            {customer.registration_number && (
              <Text style={styles.addressLine}>
                Reg: {customer.registration_number}
              </Text>
            )}
            {customer.vat_number && (
              <Text style={styles.addressLine}>
                VAT: {customer.vat_number}
              </Text>
            )}
          </View>
        </View>

        {/* DATES */}
        <View style={styles.datesGrid}>
          <View style={styles.dateCol}>
            <Text style={styles.dateLabel}>Issue Date:</Text>
            <Text style={styles.dateValue}>{invoice.issue_date}</Text>
            <Text style={styles.dateLabel}>Due Date:</Text>
            <Text style={styles.dateValue}>{invoice.due_date}</Text>
            {invoice.period_start && invoice.period_end && (
              <>
                <Text style={styles.dateLabel}>Service Period:</Text>
                <Text style={styles.dateValue}>
                  {invoice.period_start} → {invoice.period_end}
                </Text>
              </>
            )}
            {invoice.paid_at && (
              <>
                <Text style={styles.dateLabel}>Paid On:</Text>
                <Text style={[styles.dateValue, { color: '#047857' }]}>
                  {new Date(invoice.paid_at).toLocaleDateString('en-ZA')}
                </Text>
              </>
            )}
          </View>
          <View style={[styles.dateCol, { alignItems: 'flex-end' }]}>
            <Text style={styles.dateLabel}>Payment Terms:</Text>
            <Text style={styles.dateValue}>Strict prepaid (AD-13)</Text>
            <Text style={styles.dateLabel}>Status:</Text>
            <Text
              style={[
                styles.dateValue,
                { textTransform: 'uppercase', letterSpacing: 0.5 },
              ]}
            >
              {invoice.lifecycle_state ?? invoice.status}
            </Text>
          </View>
        </View>

        {/* LINE ITEMS */}
        <View style={styles.table}>
          <View style={styles.tableHead}>
            <Text style={styles.colCode}>Code</Text>
            <Text style={styles.colDesc}>Description</Text>
            <Text style={styles.colQty}>Qty</Text>
            <Text style={styles.colUnit}>Unit Price</Text>
            <Text style={styles.colVat}>VAT</Text>
            <Text style={styles.colTotal}>Total</Text>
          </View>
          {lines.map((l, i) => (
            <View
              key={l.id}
              style={[
                styles.tableRow,
                i % 2 === 1 ? styles.tableRowAlt : {},
              ]}
            >
              <Text style={styles.colCode}>{l.target_ref ?? '-'}</Text>
              <View style={styles.colDesc}>
                <Text>{l.description}</Text>
                {l.period_start && l.period_end && (
                  <Text style={{ fontSize: 7, color: '#6b7280' }}>
                    {l.period_start} → {l.period_end}
                  </Text>
                )}
              </View>
              <Text style={styles.colQty}>{l.quantity.toLocaleString()}</Text>
              {/* An item included free is shown at what it is WORTH, struck
                  through, with FREE in the total column. A bare 0.00 on a
                  printed quotation tells the customer nothing about the value
                  they are receiving — which is the whole point of the line. */}
              <Text
                style={[
                  styles.colUnit,
                  (l.discount_amount ?? 0) > 0 && l.total === 0
                    ? { textDecoration: 'line-through', color: '#6b7280' }
                    : {},
                ]}
              >
                {formatNumber(l.unit_price)}
              </Text>
              <Text style={styles.colVat}>
                {Math.round((l.vat_rate ?? 0) * 100)}%
              </Text>
              <Text style={styles.colTotal}>
                {(l.discount_amount ?? 0) > 0 && l.total === 0
                  ? 'FREE'
                  : formatNumber(l.total)}
              </Text>
            </View>
          ))}
        </View>

        {/* TOTALS */}
        <View style={styles.totalsBox}>
          <View style={styles.totalsRow}>
            <Text style={{ color: '#4b5563' }}>Subtotal:</Text>
            <Text>{formatZAR(invoice.subtotal)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={{ color: '#4b5563' }}>VAT/Tax:</Text>
            <Text>{formatZAR(invoice.vat_amount)}</Text>
          </View>
          <View style={styles.totalsRowFinal}>
            <Text>Total:</Text>
            <Text>{formatZAR(invoice.total)}</Text>
          </View>
        </View>

        {/* STUDIO PAYMENT PLAN (ST-2, 2026-07-17) */}
        {invoice.quote_metadata?.kind === 'studio_deposit' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Studio Starter payment plan</Text>
            <Text style={styles.bodyText}>
              SightHost Studio Starter total:{' '}
              {formatZAR(Number(invoice.quote_metadata.total_cents ?? 0) / 100)}
            </Text>
            <Text style={styles.bodyText}>
              Less: deposit payable now: (
              {formatZAR(Number(invoice.quote_metadata.deposit_cents ?? 0) / 100)})
            </Text>
            <Text style={styles.bodyText}>
              Balance on payment plan:{' '}
              {formatZAR(Number(invoice.quote_metadata.balance_cents ?? 0) / 100)}
            </Text>
            <Text style={[styles.bodyText, styles.bold]}>
              Payment plan: {String(invoice.quote_metadata.plan_months)} monthly
              instalments of{' '}
              {formatZAR(Number(invoice.quote_metadata.installment_cents ?? 0) / 100)}
            </Text>
            {Number(invoice.quote_metadata.rounding_discount_cents ?? 0) > 0 && (
              <Text style={styles.bodyText}>
                Includes a{' '}
                {formatZAR(Number(invoice.quote_metadata.rounding_discount_cents) / 100)}{' '}
                rounding discount.
              </Text>
            )}
          </View>
        )}

        {/* PAYMENT DETAILS */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Payment Details</Text>
          <Text style={styles.bodyText}>Bank: {siteConfig.bank.name}</Text>
          <Text style={styles.bodyText}>
            Account name: {siteConfig.bank.accountName}
          </Text>
          <Text style={styles.bodyText}>
            Account: {siteConfig.bank.accountNumber}
          </Text>
          <Text style={styles.bodyText}>
            Branch Code: {siteConfig.bank.branchCode}
          </Text>
          <Text style={styles.bodyText}>
            Account Type: {siteConfig.bank.accountType}
          </Text>
          {siteConfig.bank.swiftCode && (
            <Text style={styles.bodyText}>
              SWIFT Code: {siteConfig.bank.swiftCode}
            </Text>
          )}
          <Text style={styles.bodyText}>
            Reference:{' '}
            <Text style={styles.bold}>{invoice.number}</Text> ·{' '}
            {siteConfig.bank.referenceHint}
          </Text>
        </View>

        {/* PAYMENTS RECEIVED */}
        {payments.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Payments Received</Text>
            <View style={styles.paymentTable}>
              <View style={styles.paymentHead}>
                <Text style={styles.payDate}>Date</Text>
                <Text style={styles.payMethod}>Method</Text>
                <Text style={styles.payRef}>Reference</Text>
                <Text style={styles.payAmount}>Amount</Text>
              </View>
              {payments.map((p) => (
                <View key={p.id} style={styles.paymentRow}>
                  <Text style={styles.payDate}>
                    {p.paid_at
                      ? new Date(p.paid_at).toLocaleDateString('en-ZA')
                      : '—'}
                  </Text>
                  <Text style={styles.payMethod}>{p.gateway}</Text>
                  <Text style={styles.payRef}>
                    {p.gateway_transaction_id ?? '—'}
                  </Text>
                  <Text style={styles.payAmount}>{formatZAR(p.amount)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* FOOTER */}
        <View style={styles.footer} fixed>
          {isQuotation && (
            <Text>
              This is a quotation, not a tax invoice and not a demand for
              payment. Paying the amount accepts this quotation.
            </Text>
          )}
          <Text>
            {invoice.lifecycle_state === 'paid'
              ? 'Payment received in full. Thank you for your business!'
              : 'Thank you for your business!'}
          </Text>
          <Text>Company Registration: {siteConfig.registration}</Text>
          <Text>
            © {new Date().getFullYear()} {siteConfig.legalName}. All rights
            reserved.
          </Text>
        </View>
      </Page>
    </Document>
  );
}
